import type { FC, RefObject } from "react";
import { useCallback, useContext, useEffect, useRef, useState } from "react";
import { css } from "@emotion/css";
import moment from "moment/moment";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import { green, grey, red } from "@mui/material/colors";

import { Box, Button, Icons, Tooltip } from "@analyticsMD/quil";

import APIContext from "../contexts/APIContext";
import {
  UI_ACCENT_SUGGESTION_BG,
  UI_ACCENT_SUGGESTION_BORDER,
} from "../constants/ui-colors";
import type { DenialPrevention } from "../types/DenialPrevention";
import EDD from "../types/EDD";
import PatientDisposition from "../types/PatientDisposition";
import SuggestionArasPopover from "./suggestion-aras-popover";

import SuggestionExplainabilityPopover from "./suggestion-explainability-popover";

const styles = {
  suggestionContainer: css`
    display: flex;
    align-items: center;
    background-color: ${UI_ACCENT_SUGGESTION_BG};
    padding: 0rem 0.75rem;
    border-radius: 0.4rem;
    border: 1px solid ${UI_ACCENT_SUGGESTION_BORDER};
    gap: 0.5rem;
    min-height: 2.5rem;
  `,
  suggestionContent: css`
    display: flex;
    align-items: center;
    gap: 0.5rem;
    flex: 1;
    font-size: 0.875rem;
    font-weight: 500;
    min-width: 0;
  `,
  suggestionText: css`
    color: ${grey[900]};
    font-weight: 400;
    font-size: 0.875rem;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  `,
  suggestionTextWithTooltip: css`
    color: ${grey[900]};
    font-weight: 400;
    cursor: pointer;
    border: none;
    border-bottom: 1px dotted ${grey[900]};
    text-decoration: none;
    font-size: 0.875rem;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    background: transparent;
    padding: 0;
    font-family: inherit;
    text-align: left;
  `,
  actionButtons: css`
    display: flex;
    gap: 0.8rem;
    align-items: center;
  `,
  pacScoresTrigger: css`
    flex-shrink: 0;
    color: ${grey[700]};
    font-weight: 500;
    font-size: 0.6875rem;
    cursor: pointer;
    border: none;
    border-bottom: 1px dotted ${grey[700]};
    text-decoration: none;
    background: transparent;
    padding: 0;
    font-family: inherit;
    line-height: 1;
    letter-spacing: 0.02em;
  `,
};

const HOVER_CLOSE_DELAY_MS = 150;

const sxStyles = {
  actionButton: {
    borderRadius: "50%",
    padding: 0,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    minWidth: "auto",
  },
  acceptButton: {
    color: green[400],
    "&:focus": {
      outline: `2px solid ${green[400]}`,
      outlineOffset: "2px",
    },
    svg: {
      fontSize: "0.75rem",
    },
  },
  rejectButton: {
    color: red[600],
    "&:focus": {
      outline: `2px solid ${red[600]}`,
      outlineOffset: "2px",
    },
    svg: {
      fontSize: "0.75rem",
    },
  },
  simpleTooltip: {
    "&.MuiTooltip-tooltip": {
      width: "auto",
    },
  },
};

export type SuggestionConfirmationUpdateFn = (
  status: "ACCEPTED" | "REJECTED",
  suggestion: PatientDisposition | EDD,
) => void;

interface SuggestionConfirmationProps {
  suggestionType: string;
  suggestion: PatientDisposition | EDD;
  updateFn: SuggestionConfirmationUpdateFn;
  disabled?: boolean;
  /** When set, the explainability popover anchors to this element (e.g. wrapper around the select above the suggestion). */
  explainabilityAnchorRef?: RefObject<HTMLElement | null>;
}

const SuggestionConfirmation: FC<SuggestionConfirmationProps> = ({
  suggestionType,
  suggestion,
  updateFn,
  disabled,
  explainabilityAnchorRef,
}) => {
  /** Fallback anchor: full suggestion bar (used when no select wrapper ref is passed). */
  const popoverAnchorRef = useRef<HTMLDivElement>(null);

  const detailAnchorEl =
    explainabilityAnchorRef?.current ?? popoverAnchorRef.current;

  let typedSuggestion: PatientDisposition | EDD;
  let suggestionValue: string;
  if (suggestionType !== "disposition") {
    typedSuggestion = suggestion as EDD;
    suggestionValue = moment(typedSuggestion.edd_value).format(
      "ddd MM/DD/YYYY",
    );
  } else {
    typedSuggestion = suggestion as PatientDisposition;
    suggestionValue = typedSuggestion.disposition.display_name;
  }

  const { configurationQuery } = useContext(APIContext);
  const isSuggestionExplainabilityEnabled =
    configurationQuery?.data?.[0]?.suggestion_explainability_enabled ?? true;

  const suggestionContext = suggestion?.suggestion_context;
  const denialPrevention: DenialPrevention | null =
    suggestionType === "disposition"
      ? ((suggestion as PatientDisposition).denial_prevention ?? null)
      : null;
  const hasExplainability =
    suggestionContext != null && isSuggestionExplainabilityEnabled;
  const hasDenialPrevention = denialPrevention != null;
  const hasBothPanels = hasExplainability && hasDenialPrevention;
  const hasDetailPopover = hasExplainability || hasDenialPrevention;

  const [explainOpen, setExplainOpen] = useState(false);
  const [arasOpen, setArasOpen] = useState(false);

  const explainCloseTimerRef = useRef<ReturnType<typeof setTimeout> | null>(
    null,
  );
  const arasCloseTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clearExplainCloseTimer = useCallback(() => {
    if (explainCloseTimerRef.current != null) {
      clearTimeout(explainCloseTimerRef.current);
      explainCloseTimerRef.current = null;
    }
  }, []);

  const clearArasCloseTimer = useCallback(() => {
    if (arasCloseTimerRef.current != null) {
      clearTimeout(arasCloseTimerRef.current);
      arasCloseTimerRef.current = null;
    }
  }, []);

  const scheduleExplainClose = useCallback(() => {
    clearExplainCloseTimer();
    explainCloseTimerRef.current = setTimeout(() => {
      setExplainOpen(false);
      explainCloseTimerRef.current = null;
    }, HOVER_CLOSE_DELAY_MS);
  }, [clearExplainCloseTimer]);

  const scheduleArasClose = useCallback(() => {
    clearArasCloseTimer();
    arasCloseTimerRef.current = setTimeout(() => {
      setArasOpen(false);
      arasCloseTimerRef.current = null;
    }, HOVER_CLOSE_DELAY_MS);
  }, [clearArasCloseTimer]);

  useEffect(() => {
    return () => {
      clearExplainCloseTimer();
      clearArasCloseTimer();
    };
  }, [clearExplainCloseTimer, clearArasCloseTimer]);

  const explainPaperProps = {
    onMouseEnter: () => {
      clearExplainCloseTimer();
    },
    onMouseLeave: () => {
      scheduleExplainClose();
    },
  };

  const arasPaperProps = {
    onMouseEnter: () => {
      clearArasCloseTimer();
    },
    onMouseLeave: () => {
      scheduleArasClose();
    },
  };

  const suggestionTextElement = (
    <span className={styles.suggestionText}>{suggestionValue}</span>
  );

  const arasTriggerLabel =
    denialPrevention?.prevention_type != null &&
    denialPrevention.prevention_type.length > 0
      ? denialPrevention.prevention_type
      : "ARAS";

  const explainabilityTrigger = (
    <button
      type="button"
      className={styles.suggestionTextWithTooltip}
      aria-expanded={explainOpen}
      aria-haspopup="dialog"
      onMouseEnter={(e) => {
        e.stopPropagation();
        clearExplainCloseTimer();
        clearArasCloseTimer();
        setArasOpen(false);
        setExplainOpen(true);
      }}
      onMouseLeave={(e) => {
        e.stopPropagation();
        scheduleExplainClose();
      }}
    >
      {suggestionValue}
    </button>
  );

  const denialPreventionOnlyTrigger = (
    <button
      type="button"
      className={styles.suggestionTextWithTooltip}
      aria-expanded={arasOpen}
      aria-haspopup="dialog"
      onMouseEnter={(e) => {
        e.stopPropagation();
        clearExplainCloseTimer();
        clearArasCloseTimer();
        setExplainOpen(false);
        setArasOpen(true);
      }}
      onMouseLeave={(e) => {
        e.stopPropagation();
        scheduleArasClose();
      }}
    >
      {suggestionValue}
    </button>
  );

  const denialPreventionSecondaryTrigger = (
    <button
      type="button"
      className={styles.pacScoresTrigger}
      aria-expanded={arasOpen}
      aria-haspopup="dialog"
      aria-label={`View ${arasTriggerLabel} denial-prevention scores`}
      onMouseEnter={(e) => {
        e.stopPropagation();
        clearExplainCloseTimer();
        clearArasCloseTimer();
        setExplainOpen(false);
        setArasOpen(true);
      }}
      onMouseLeave={(e) => {
        e.stopPropagation();
        scheduleArasClose();
      }}
    >
      {arasTriggerLabel}
    </button>
  );

  return (
    <Box sx={{ width: "100%" }}>
      <div ref={popoverAnchorRef} className={styles.suggestionContainer}>
        <Icons.QBadgeIcon />
        <div className={styles.suggestionContent}>
          {hasDetailPopover ? (
            <>
              {hasExplainability ? (
                <>
                  {explainabilityTrigger}
                  {hasBothPanels ? denialPreventionSecondaryTrigger : null}
                </>
              ) : (
                denialPreventionOnlyTrigger
              )}
              {hasExplainability ? (
                <SuggestionExplainabilityPopover
                  open={explainOpen}
                  anchorEl={explainOpen ? detailAnchorEl : null}
                  onClose={() => {
                    clearExplainCloseTimer();
                    setExplainOpen(false);
                  }}
                  suggestion={typedSuggestion}
                  suggestionType={suggestionType}
                  paperProps={explainPaperProps}
                />
              ) : null}
              {hasDenialPrevention && denialPrevention != null ? (
                <SuggestionArasPopover
                  open={arasOpen}
                  anchorEl={arasOpen ? detailAnchorEl : null}
                  onClose={() => {
                    clearArasCloseTimer();
                    setArasOpen(false);
                  }}
                  denialPrevention={denialPrevention}
                  paperProps={arasPaperProps}
                />
              ) : null}
            </>
          ) : (
            <Tooltip
              title={suggestionValue}
              placement="right"
              slotProps={{
                tooltip: {
                  sx: sxStyles.simpleTooltip,
                },
              }}
            >
              {suggestionTextElement}
            </Tooltip>
          )}
        </div>
        {!disabled && (
          <div className={styles.actionButtons}>
            <Button
              sx={{ ...sxStyles.actionButton, ...sxStyles.acceptButton }}
              onClick={() => updateFn("ACCEPTED", suggestion)}
              aria-label="Accept suggestion"
            >
              <CheckIcon
                style={{
                  width: "1.25rem",
                  height: "1.25rem",
                  padding: "0.125rem",
                }}
              />
            </Button>
            <Button
              sx={{ ...sxStyles.actionButton, ...sxStyles.rejectButton }}
              onClick={() => updateFn("REJECTED", suggestion)}
              aria-label="Reject suggestion"
            >
              <CloseIcon
                style={{
                  width: "1.25rem",
                  height: "1.25rem",
                  padding: "0.125rem",
                }}
              />
            </Button>
          </div>
        )}
      </div>
    </Box>
  );
};

export default SuggestionConfirmation;
